import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, CheckList, FAQAccordion, CTABand, Rise, Eyebrow } from "@/components/chrome";
import { PROVINCES, CITY_COUNT } from "@/data/provinces";
import { findService, paths } from "@/lib/seo";
import NotFound from "@/pages/NotFound";

export default function ServicePage() {
  const { serviceSlug } = useParams();
  const service = findService(serviceSlug);
  if (!service) return <NotFound />;

  const crumbs = [{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: service.name }];

  return (
    <div data-testid="service-page">
      <PageHero prefix="service" crumbs={crumbs} eyebrow={service.category}
        title={`${service.name} — across Canada`} lead={service.tagline} />

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="service-overview">
        <div className="max-w-3xl space-y-5">
          {service.description.map((p, i) => (
            <p key={i} className="leading-relaxed text-slate-600 sm:text-lg">{p}</p>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="service-included">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>The checklist</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              What's included in every {service.short.toLowerCase()} visit
            </h2></Rise>
          <div className="mt-8"><CheckList items={service.includes} testid="service-checklist" /></div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="service-process">
        <Rise><Eyebrow>How it works</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Our process</h2></Rise>
        <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {service.process.map((step, i) => (
            <Rise key={step.t} delay={i * 0.06} className="border-t border-slate-200 pt-5">
              <p className="font-mono text-sm font-semibold text-cyan-700">0{i + 1}</p>
              <h3 className="mt-2 font-display text-base font-semibold text-slate-900">{step.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.d}</p>
            </Rise>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="service-availability">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>Availability</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {service.short} in {CITY_COUNT}+ Canadian cities
            </h2>
            <p className="mt-4 max-w-2xl leading-relaxed text-slate-600">
              Select your city for local pricing, coverage details, and FAQs for {service.short.toLowerCase()} in your area.
            </p></Rise>
          <div className="mt-10 space-y-8">
            {PROVINCES.map((p) => (
              <div key={p.slug}>
                <Link to={paths.province(p.slug)} data-testid={`service-province-${p.slug}`}
                  className="font-display text-base font-semibold text-slate-900 transition-colors hover:text-cyan-700">
                  {p.name}
                </Link>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm">
                  {p.cities.map((c) => (
                    <Link key={c.slug} to={paths.serviceCity(p.slug, c.slug, service.slug)}
                      data-testid={`service-city-${service.slug}-${c.slug}`}
                      className="inline-flex items-center gap-1 text-slate-600 transition-colors hover:text-cyan-700">
                      {service.short} {c.name} <ArrowRight className="h-3 w-3 text-slate-300" />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="service-faq">
        <Rise><Eyebrow>FAQ</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {service.name} — common questions
          </h2></Rise>
        <div className="mt-8 max-w-3xl">
          <FAQAccordion faqs={service.faqs.map((f) => ({ q: f.q.replaceAll("{city}", "your city").replaceAll("{phone}", "807 697-5399").replaceAll("{email}", "admin@tbaycleaners.com"), a: f.a.replaceAll("{city}", "your city").replaceAll("{serviceLower}", service.short.toLowerCase()).replaceAll("{code}", "").replaceAll("{phone}", "807 697-5399").replaceAll("{email}", "admin@tbaycleaners.com").replaceAll("{province}", "your province") }))} testid="service-faq" />
        </div>
      </section>

      <CTABand title={`Book ${service.short.toLowerCase()} anywhere in Canada`}
        lead="One call to a real coordinator. Firm flat-rate quote, usually within one business hour."
        prefix="service-cta" />
    </div>
  );
}
